import React, { FC, Fragment, useEffect } from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { currentOrgVar } from './apollo/reactive-variables/organization';
import { currentUserVar } from './apollo/reactive-variables/user';
import ReactGA from 'react-ga4';
import { CONSTANTS } from 'config/constants';
import { isExpectedErrorMessage, isGatewayErrorMessage } from './error-reporting/expected-errors';
import { buildCrashSummary, rememberCrash } from './error-reporting/crash-summary';
import CrashReport from './error-reporting/crash-report.component';

type ErrorFallbackProps = {
    error: any;
    resetErrorBoundary: () => void;
};
const ErrorFallback: FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
    const { t: translate } = useTranslation(['common']);
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();
    let shouldShowError = false;
    let shouldShowGatewayError = false;

    if (!isExpectedErrorMessage(error?.message)) {
        shouldShowError = true;
    }
    if (isGatewayErrorMessage(error?.message)) {
        shouldShowGatewayError = true;
    }

    useEffect(() => {
        if (shouldShowError) {
            // DD-3780: after the reload the help assistant offers help with this crash. Deferred
            // one tick so the error boundary's report (and its GlitchTip id) is in first.
            setTimeout(() => rememberCrash(buildCrashSummary(error)), 0);
        }
        if (shouldShowError || shouldShowGatewayError) {
            localStorage.clear();
            shouldShowError ? ReactGA.event('error-screen') : ReactGA.event('gateway-error-screen');
        }
    }, []);

    return (
        <Fragment>
            {shouldShowGatewayError ? (
                <div
                    role="alert"
                    style={{
                        maxWidth: 700,
                        fontFamily: 'sans-serif',
                        margin: 'auto',
                        marginTop: 80,
                    }}
                >
                    <Alert severity="info">
                        <AlertTitle>
                            <h1 style={{ marginTop: 0 }}>{translate('GATEWAY_ERROR_PAGE_TITLE')}</h1>
                        </AlertTitle>
                        <Box mb={2}>{translate('GATEWAY_ERROR_PAGE_CONTENT')}</Box>
                        <Button
                            variant="contained"
                            onClick={() => {
                                resetErrorBoundary();
                                window.location.reload();
                            }}
                        >
                            {translate('GATEWAY_ERROR_PAGE_RELOAD')}
                        </Button>
                    </Alert>
                </div>
            ) : undefined}
            {shouldShowError ? (
                <div
                    role="alert"
                    style={{
                        maxWidth: 700,
                        fontFamily: 'sans-serif',
                        margin: 'auto',
                        marginTop: 80,
                    }}
                >
                    <Alert severity="error">
                        <AlertTitle>
                            <h1 style={{ marginTop: 0 }}>{translate('ERROR_PAGE_TITLE')}</h1>
                        </AlertTitle>
                        <Box mb={2}>{translate('ERROR_PAGE_CONTENT')}</Box>
                        <Button
                            variant="contained"
                            onClick={() => {
                                resetErrorBoundary();
                                window.location.href = document.referrer;
                            }}
                        >
                            {translate('ERROR_PAGE_RELOAD')}
                        </Button>
                        <CrashReport error={error} />
                    </Alert>
                </div>
            ) : undefined}
        </Fragment>
    );
};

export default ErrorFallback;
